/**
 * DataTable — generic shadcn-style table built on TanStack Table v8.
 *
 * Phase-2 Wave-2 W2.T1 primitive. The Phase-2 Employees page (W3) uses this
 * to render the rows for each `?status=` tab, so the props are tuned for
 * server-side state — sorting and pagination are owned by the parent and
 * passed down as controlled values.
 *
 * Why server-side state by default
 * --------------------------------
 *   The user list can grow into the thousands; client-side sort/page would
 *   force the FE to fetch all rows up front (research-A §3, research-B §4.h
 *   — pagination is offset-based per V2-FIX). Keeping `sorting` and
 *   `pagination` controlled means the parent can translate them into
 *   `?sortBy=…&sortDir=…&page=…&limit=…` query params and refetch.
 *
 * A11y
 * ----
 *   - `aria-sort` is set on each sortable header per its current direction
 *     (ascending / descending / none).
 *   - Sortable header is a `<button>` so screen readers announce the role
 *     and Space/Enter both toggle the sort.
 *   - Body rows are `tabIndex={0}` when `onRowClick` is set; Up/Down arrows
 *     move roving focus, Enter activates the row, mirroring DataGrid
 *     semantics that the page-level keyboard nav (W3) plugs into.
 *   - Loading state renders 8 skeleton rows under the column heads so the
 *     screen-reader user hears a `role="status"` live region while the
 *     data refetches (Skeleton primitive already announces `aria-busy`).
 */
import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type OnChangeFn,
  type Row,
  type Table as TableInstance,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface DataTableProps<TData> {
  /** Column definitions in TanStack Table v8 shape. */
  columns: ColumnDef<TData, unknown>[];
  /** Row data — already paginated/filtered by the server. */
  data: TData[];
  /** Controlled sorting state (server-side). */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /**
   * When true, render a fixed number of skeleton rows instead of `data`.
   * Use during the initial fetch — the table head is still rendered so
   * the layout doesn't jump when rows arrive.
   */
  loading?: boolean;
  /**
   * Slot rendered when `data.length === 0 && !loading`. Passed as a node so
   * the parent can show domain-specific copy (research-A §8 Q5 variants).
   */
  emptyState?: React.ReactNode;
  /** Row click handler. When set, rows become focusable + keyboard-actionable. */
  onRowClick?: (row: TData) => void;
  /** Extra class on the outer wrapper. */
  className?: string;
  /**
   * Number of skeleton rows to render in `loading` state. Defaults to 8 —
   * matches the page-size selector's smallest option (research-B §4.h).
   */
  skeletonRows?: number;
  /**
   * Optional id prefix for `data-testid` on rows. The DataTable spec relies
   * on it; consumers can override to namespace multiple tables on a page.
   */
  testIdPrefix?: string;
  /**
   * Optional `data-testid` for the empty-state cell. Defaults to a value
   * that does NOT share the `testIdPrefix` namespace, so a query like
   * `[data-testid^="${testIdPrefix}-"]` cannot accidentally match the
   * empty-state row alongside real data rows. Pass an explicit value to
   * override the default.
   */
  emptyStateTestId?: string;
}

const RESOLVED_AS_NONE = false as const;

function NextSortIcon({ direction }: { direction: 'asc' | 'desc' | typeof RESOLVED_AS_NONE }) {
  if (direction === 'asc') return <ChevronUp className="size-3.5" aria-hidden="true" />;
  if (direction === 'desc') return <ChevronDown className="size-3.5" aria-hidden="true" />;
  return <ChevronsUpDown className="size-3.5 opacity-60" aria-hidden="true" />;
}

/**
 * Server-side DataTable. Generic over `TData` so the column accessors stay
 * type-safe in the consumer.
 */
export function DataTable<TData>({
  columns,
  data,
  sorting,
  onSortingChange,
  loading = false,
  emptyState,
  onRowClick,
  className,
  skeletonRows = 8,
  testIdPrefix = 'data-table-row',
  emptyStateTestId,
}: DataTableProps<TData>): React.ReactElement {
  // The default empty-state testid lives OUTSIDE the row-prefix namespace so
  // a `[data-testid^="${testIdPrefix}-"]` selector can't accidentally pick
  // up the empty-state cell alongside real rows. e.g. testIdPrefix
  // "employees-row" → empty-state testid "employees-empty".
  const resolvedEmptyTestId =
    emptyStateTestId ??
    (testIdPrefix.endsWith('-row')
      ? `${testIdPrefix.slice(0, -'-row'.length)}-empty`
      : `${testIdPrefix}-empty-state`);
  const table: TableInstance<TData> = useReactTable<TData>({
    data,
    columns,
    state: { sorting: sorting ?? [] },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    // Server-side sort: we don't run the row-model's sort fn locally.
    manualSorting: true,
    enableSortingRemoval: true,
  });

  // Roving keyboard focus across rows (Up/Down) + Enter to activate. Only
  // wired up when `onRowClick` is set so static tables stay tab-flat.
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const focusRow = React.useCallback((index: number) => {
    const root = containerRef.current;
    if (!root) return;
    const rows = root.querySelectorAll<HTMLTableRowElement>('tbody tr[data-row-focusable="true"]');
    const target = rows[Math.max(0, Math.min(index, rows.length - 1))];
    target?.focus();
  }, []);

  const handleRowKey = React.useCallback(
    (event: React.KeyboardEvent<HTMLTableRowElement>, row: Row<TData>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onRowClick?.(row.original);
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusRow(row.index + 1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusRow(row.index - 1);
      }
    },
    [focusRow, onRowClick],
  );

  const showEmpty = !loading && data.length === 0;

  return (
    <div
      ref={containerRef}
      className={cn('overflow-x-auto rounded-md border border-line bg-surface', className)}
    >
      <table className="w-full caption-bottom text-sm">
        <thead className="border-b border-line bg-surface-2">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sortDir = header.column.getIsSorted() || RESOLVED_AS_NONE;
                const ariaSort: React.AriaAttributes['aria-sort'] =
                  sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none';
                // FT.D3 / UI-MF3: respect column.meta.hideOn — CSS-only
                // narrow-viewport hide so SSR/no-JS users get the same
                // layout as the live page.
                const hideClass =
                  header.column.columnDef.meta?.hideOn === '<lg'
                    ? 'hidden lg:table-cell'
                    : undefined;
                return (
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={canSort ? ariaSort : undefined}
                    className={cn(
                      'h-10 px-3 text-left align-middle text-xs font-medium uppercase tracking-wide text-muted',
                      hideClass,
                    )}
                    style={{ width: header.getSize() === 150 ? undefined : header.getSize() }}
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        // Touch-target (impeccable audit P2 / WCAG 2.5.5):
                        // the inline `py-0.5` sort toggle is ~20px tall — far
                        // under 44px. Floor its min-height on COARSE pointers
                        // only; the header cell grows to contain it on touch
                        // while `pointer: fine` keeps the compact 20px tap area.
                        className="inline-flex items-center gap-1.5 rounded-sm py-0.5 text-left text-xs font-medium uppercase tracking-wide text-muted transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:min-h-[44px]"
                      >
                        <span>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        <NextSortIcon direction={sortDir} />
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: skeletonRows }).map((_, rowIdx) => (
              <tr key={`skeleton-${rowIdx}`} data-testid={`${testIdPrefix}-skeleton`}>
                {columns.map((col, colIdx) => {
                  const hideClass = col.meta?.hideOn === '<lg' ? 'hidden lg:table-cell' : undefined;
                  return (
                    <td key={`skeleton-${rowIdx}-${colIdx}`} className={cn('px-3 py-3', hideClass)}>
                      <Skeleton className="h-4 w-full max-w-[180px]" />
                    </td>
                  );
                })}
              </tr>
            ))
          ) : showEmpty ? (
            <tr>
              <td
                colSpan={columns.length || 1}
                className="px-3 py-12 text-center text-sm text-muted"
                data-testid={resolvedEmptyTestId}
              >
                {emptyState ?? 'Нет данных'}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              const focusable = Boolean(onRowClick);
              return (
                <tr
                  key={row.id}
                  data-testid={`${testIdPrefix}-${row.index}`}
                  data-row-focusable={focusable ? 'true' : undefined}
                  tabIndex={focusable ? 0 : undefined}
                  onClick={focusable ? () => onRowClick?.(row.original) : undefined}
                  onKeyDown={focusable ? (e) => handleRowKey(e, row) : undefined}
                  className={cn(
                    'border-b border-line/60 last:border-0 transition-colors',
                    focusable &&
                      'cursor-pointer hover:bg-surface-2 focus:outline-none focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const hideClass =
                      cell.column.columnDef.meta?.hideOn === '<lg'
                        ? 'hidden lg:table-cell'
                        : undefined;
                    return (
                      <td
                        key={cell.id}
                        className={cn('px-3 py-2.5 align-middle text-ink', hideClass)}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Column meta extensions ──────────────────────────────────────────────
//
// FT.D3 (closes UI-MF3): a column can opt into responsive hiding by
// declaring `meta: { hideOn: '<lg' }`. Implementation is CSS-only:
// the `<th>` and `<td>` get `hidden lg:table-cell`, so neither layout
// nor the row's child structure depends on JS state. The header cell
// stays in the DOM (preserving column-index alignment) but is
// visually hidden, mirroring how Tailwind's `hidden` class normally
// behaves.
//
// We deliberately do NOT support arbitrary breakpoints here — adding
// more knobs invites premature complexity. If a future surface needs
// `<md` or `<xl`, extend the union. For Phase 4 «Проектов / ИНН /
// Email» on the clients table, `<lg` is the only pivot.

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /**
     * Hide this column below the named Tailwind breakpoint. Currently only
     * `'<lg'` is implemented (1024 px floor). Affects both header `<th>`
     * and body `<td>` in the same render pass.
     */
    hideOn?: '<lg';
  }
}

import type { RowData } from '@tanstack/react-table';

// Re-export TanStack column helpers so consumers don't need a second import.
export type { ColumnDef, SortingState } from '@tanstack/react-table';
export { createColumnHelper } from '@tanstack/react-table';
